<script lang="ts">
import { storeToRefs } from "pinia"
import offlineTablePageInit from "../../utils/offlineTablePageInit.vue"
import OfflineChip from "../../components/Offline/Chip.vue"
import { useEpciCommuneStore, useOnlineStore, useUserStore } from "~/store"
import ModalImportExportDossiers from "~/components/modal/importExportDossiers.vue"
import ModalInventaire from "~/components/modal/inventaire.vue"

export default {
    name: "ListDossiers",
    components: { OfflineChip, ModalImportExportDossiers, ModalInventaire },
    extends: offlineTablePageInit,
    setup() {
        useHead({
            title: "Larchiviste | Dossiers",
            meta: [
                { name: "description", content: "Gestion des dossiers" },
            ],
        })
        definePageMeta({
            middleware: ["auth"],
            layout: false,
        })
        const communeStore = useEpciCommuneStore()
        const { currentEpciCommune } = storeToRefs(communeStore)

        const onlineStore = useOnlineStore()
        const { online, onlineReady } = storeToRefs(onlineStore)

        const { currentUser } = useUserStore()

        return {
            online,
            currentEpciCommune,
            onlineReady,
            currentUser,
        }
    },
    data() {
        return {
            permissionName: "dossier",
            requestName: "archive-records",
            defaultUrl: "/dossiers",
            translationName: "dossiers",
            valueAsViewLink: ["cote"],
            globalQuery: null,
            initSort: {
                key: "cote",
                order: "asc",
            },
            importExportShow: false,
            inventaireShow: false,
        }
    },
    computed: {
        columns() {
            const cols = [
                { dataKey: "cote", transform: [this.isCote] },
                { dataKey: "series.v" },
                { dataKey: "sub_series.name" },
                { dataKey: "thematic" },
                { dataKey: "typology" },
                { dataKey: "min_year" },
                { dataKey: "max_year" },
                { dataKey: "box.number" },
                { dataKey: "updated_at", transform: [this.isNumericDate] },
                { dataKey: "created_at", transform: [this.isNumericDate] },
                { dataKey: "communication_delay", transform: [this.getDurationValue] },
                { dataKey: "status", transform: [this.toTranslate], template: [this.isChips] },
            ]
            if (this.hasAssociatedEpciCommune) {
                return [
                    { dataKey: "epci_commune.v" },
                    ...cols,
                ]
            }
            return cols
        },
        filtersOverload() {
            const baseFilters = ["cote", "status"]
            if (this.hasAssociatedEpciCommune) {
                return ["epci_commune_id", ...baseFilters]
            }
            return baseFilters
        },
        moreFiltersOverload() {
            const onlineList = [
                "cote",
                "status",
                "thematic",
                "typology",
                "where_in_hamlet",
                "classification_type",
                "min_year",
                "max_year",
                "series_id",
                "sub_series_id",
                "regulatory_elimination_year",
                "box_id",
            ]
            if (this.online && this.hasAssociatedEpciCommune) {
                return ["epci_commune_id", ...onlineList]
            }
            const offLineList = [
                "cote",
                "status",
                "thematic",
                "typology",
                "classification_type",
                "min_year",
                "max_year",
                "series_id",
                "sub_series_id",
                "regulatory_elimination_year",
                "box_id",
            ]
            if (this.hasAssociatedEpciCommune) {
                return ["epci_commune_id", ...offLineList]
            }
            return offLineList
        },

        customQuery() {
            return this.currentEpciCommune?.id
                ? { customQueryKey: "limit_to_accessible_for", queryId: this.currentEpciCommune?.id }
                : null
        },
        canUpdateDossiers(): boolean {
            return this.currentUser?.role === "admin" && this.currentEpciCommune?.id && this.$permissionCheck("dossier_export")
        },
        customFilters() {
            if (!this.filters)
                return []
            if (!this.hasAssociatedEpciCommune)
                return this.filters

            return this.filters.map((filter: any) => {
                if ((filter.name === "epci_commune_id" || filter.name === "where_in_epci_commune") && this.currentEpciCommune?.id) {
                    const modifiedFilter = { ...filter }

                    if (modifiedFilter.autocomplete_url && !modifiedFilter.autocomplete_url.includes("limit_to_accessible_for")) {
                        const separator = modifiedFilter.autocomplete_url.includes("?") ? "&" : "?"
                        modifiedFilter.autocomplete_url += `${separator}filter[limit_to_accessible_for]=${this.currentEpciCommune.id}`
                    }

                    return modifiedFilter
                }
                return filter
            })
        },
        hasAssociatedEpciCommune(): boolean {
            return this.currentEpciCommune?.accessible_communes?.length > 0
        },
    },
    watch: {
        onlineReady(val) {
            if (val) {
                this.loadData()
            }
        },
        customQuery() {
            this.loadData()
        },
    },
    mounted() {
        this.loading = true
    },
    methods: {
        getDurationValue(key, value) {
            if (value && value.duration) {
                return `${value.duration} ${this.toTranslate(`${key}.unit`, value.unit)}`
            }
            if (value && value.comment) {
                return value.comment
            }
            return "-"
        },
        isCote(key, value) {
            if (!value) {
                return "Inconnue"
            }
            return value
        },
    },
}
</script>

<template>
    <NuxtLayout name="default">
        <template #title>
            <div class="me-auto">
                {{ $translate('dossiers.list') }}
            </div>
            <OfflineChip />
        </template>
        <div id="dossiers">
            <div class="d-flex">
                <ListActionsButtons
                    v-if="currentEpciCommune && currentEpciCommune.id && $permissionCheck(`${permissionName}_store`)"
                    :query="$refs?.filters?.query"
                    :request-name
                    :translation-name
                    :exportable
                    :default-url
                    :permission-name
                    class-override="mb-1"
                />
                <v-btn
                    v-if="online"
                    color="primary"
                    prepend-icon="mdi-file-arrow-up-down-outline"
                    :text="canUpdateDossiers ? 'Import / Export' : 'Export'"
                    @click="importExportShow = !importExportShow"
                />
                <v-spacer />
                <v-btn
                    v-if="online"
                    color="primary"
                    prepend-icon="mdi-download-outline"
                    text="Inventaire"
                    @click="inventaireShow = !inventaireShow"
                />
            </div>
            <ListOfflineFilters
                v-if="filters"
                ref="filters"
                :permission-name
                :loading
                :filters="customFilters"
                :translation-name
                :more-filters-overload
                :filters-overload
                @load-data="loadData()"
                @reset-pagination="$refs.pagination.resetPagination()"
            />
            <v-card class="pa-5">
                <ListDossiersMassEditButton
                    v-if="online && currentEpciCommune && currentEpciCommune.id"
                    :disabled="!data || data.length === 0 || loading || $refs.table?.selectedData.length === 0"
                    :selected-data="$refs.table?.selectedData || []"
                    translation-name="dossiers"
                    @load-data="loadData()"
                />
                <ListTable
                    v-if="currentEpciCommune?.id"
                    ref="table"
                    :value-as-view-link="valueAsViewLink"
                    :is-selectable="!!(online && currentEpciCommune && currentEpciCommune.id)"
                    :permission-name
                    :translation-name
                    :columns
                    :sortable
                    :default-url
                    :loading
                    :data
                    :init-sort
                    @reset-pagination="$refs.pagination.resetPagination()"
                    @load-data="loadData()"
                    @delete-data="deleteData($event)"
                />

                <div>
                    <ListPagination
                        ref="pagination"
                        :total
                        :loading
                        :last-page
                        @load-data="loadData"
                    />
                </div>
            </v-card>
        </div>
    </NuxtLayout>
    <ModalImportExportDossiers v-model:show="importExportShow" @imported="loadData()" />
    <ModalInventaire v-model:show="inventaireShow" :epci-commune-input="currentEpciCommune" />
</template>
